import { InfiniteChoices, ChoiceRecord } from './infinite_choices';

export type AssignedCompany = { id: string; name: string; domain: string };
export type AssignedUser = { id: string; name: string; email: string };
export type AssignmentEntry = { user?: AssignedUser; company?: AssignedCompany };

const COMPANIES_URL = '/meta_apis/campaign_assignments/companies';
const ASSIGNEES_URL = '/meta_apis/campaign_assignments/assignees';

const COMPANY_PLACEHOLDER = 'Search by company name';
const USER_PLACEHOLDER = 'Search by user name or email';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

let companyInf: InfiniteChoices | null = null;
let userInf: InfiniteChoices | null = null;
let userFilterCompanyId = '';
let pairSeq = 0;

function setAssignError(message: string | null): void {
  const $err = $('[data-role="assign-error"]');
  if (!message) {
    $err.addClass('d-none').text('');
    return;
  }
  $err.removeClass('d-none').text(message);
}

function nowLabel(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())} ${MONTHS[d.getMonth()]} ${pad(d.getDate())}, ${d.getFullYear()}`;
}

function assignedCompanyIds(): string[] {
  return $('[data-role="assigned-companies-body"] tr')
    .map(function() { return String($(this).attr('data-company-id') ?? ''); })
    .get();
}

function assignedUserIds(): string[] {
  return $('[data-role="assigned-users-body"] tr')
    .map(function() { return String($(this).attr('data-user-id') ?? ''); })
    .get();
}

function syncUsersToSelectedCompany(): void {
  const companyId = companyInf ? companyInf.getSelectedId() : '';
  if (companyId === userFilterCompanyId) return;
  userFilterCompanyId = companyId;
  userInf?.reset();
}

// Reset both dropdowns to a fresh state: company selection returns to the
// placeholder, any search query is cleared, the user filter is cleared, and
// freshly assigned records drop out of the lists.
function refreshDropdowns(): void {
  userFilterCompanyId = '';
  companyInf?.reset();
  userInf?.reset();
}

function refreshAssignedTables(): void {
  const companyCount = $('[data-role="assigned-companies-body"] tr').length;
  const userCount = $('[data-role="assigned-users-body"] tr').length;
  $('[data-role="assigned-companies"]').toggleClass('d-none', companyCount === 0);
  $('[data-role="assigned-users"]').toggleClass('d-none', userCount === 0);
  $('[data-role="assign-selected"]').toggleClass('d-none', companyCount === 0 && userCount === 0);
}

function buildRow(cells: string[], dataAttrs: Record<string, string>): JQuery {
  const $tr = $('<tr></tr>');
  Object.entries(dataAttrs).forEach(([key, value]) => $tr.attr(key, value));
  cells.forEach(text => $('<td></td>').text(text).appendTo($tr));
  $('<td class="text-center"></td>')
    .append('<button type="button" class="btn btn-sm btn-danger assign-remove">Remove assignee</button>')
    .appendTo($tr);
  return $tr;
}

function addCompanyRow(company: AssignedCompany, pairId?: string): void {
  if (!company.id) return;
  if ($(`[data-role="assigned-companies-body"] tr[data-company-id="${company.id}"]`).length) {
    setAssignError(`${company.name} is already assigned.`);
    return;
  }
  const dataAttrs: Record<string, string> = {
    'data-company-id': company.id, 'data-name': company.name, 'data-domain': company.domain,
  };
  if (pairId) dataAttrs['data-pair-id'] = pairId;
  const $tr = buildRow([company.name, company.domain || '—', nowLabel()], dataAttrs);
  $('[data-role="assigned-companies-body"]').append($tr);
  refreshAssignedTables();
}

function addUserRow(user: AssignedUser, pairId?: string): void {
  if (!user.id) return;
  if ($(`[data-role="assigned-users-body"] tr[data-user-id="${user.id}"]`).length) {
    setAssignError(`${user.name} is already assigned.`);
    return;
  }
  const dataAttrs: Record<string, string> = {
    'data-user-id': user.id, 'data-name': user.name, 'data-email': user.email,
  };
  if (pairId) dataAttrs['data-pair-id'] = pairId;
  const $tr = buildRow([user.name, user.email || '—', nowLabel()], dataAttrs);
  $('[data-role="assigned-users-body"]').append($tr);
  refreshAssignedTables();
}

function toAssignedUser(user: ChoiceRecord): AssignedUser {
  return { id: String(user.id), name: user.name, email: user.email ?? '' };
}

function toAssignedCompany(company: ChoiceRecord): AssignedCompany {
  return { id: String(company.id), name: company.name, domain: company.domain ?? '' };
}

function onAssign(): void {
  setAssignError(null);
  const user = userInf ? userInf.getSelected() : null;
  const company = companyInf ? companyInf.getSelected() : null;

  if (!user && !company) {
    setAssignError('Select a company or a user to assign.');
    return;
  }

  if (user && company) {
    if (assignedUserIds().includes(String(user.id))) {
      setAssignError(`${user.name} is already assigned.`);
      return;
    }
    if (assignedCompanyIds().includes(String(company.id))) {
      setAssignError(`${company.name} is already assigned.`);
      return;
    }
    const pairId = String(++pairSeq);
    addUserRow(toAssignedUser(user), pairId);
    addCompanyRow(toAssignedCompany(company), pairId);
  } else {
    if (user) addUserRow(toAssignedUser(user));
    if (company) addCompanyRow(toAssignedCompany(company));
  }

  refreshDropdowns();
}

export function getAssignedCompanies(): AssignedCompany[] {
  return $('[data-role="assigned-companies-body"] tr').map(function() {
    const $tr = $(this);
    return {
      id: String($tr.attr('data-company-id') ?? ''),
      name: String($tr.attr('data-name') ?? ''),
      domain: String($tr.attr('data-domain') ?? ''),
    };
  }).get();
}

export function getAssignedUsers(): AssignedUser[] {
  return $('[data-role="assigned-users-body"] tr').map(function() {
    const $tr = $(this);
    return {
      id: String($tr.attr('data-user-id') ?? ''),
      name: String($tr.attr('data-name') ?? ''),
      email: String($tr.attr('data-email') ?? ''),
    };
  }).get();
}

export function getAssignments(): AssignmentEntry[] {
  const pairs: Record<string, AssignmentEntry> = {};
  const singles: AssignmentEntry[] = [];

  $('[data-role="assigned-companies-body"] tr').each(function() {
    const $tr = $(this);
    const company: AssignedCompany = {
      id: String($tr.attr('data-company-id') ?? ''),
      name: String($tr.attr('data-name') ?? ''),
      domain: String($tr.attr('data-domain') ?? ''),
    };
    const pairId = String($tr.attr('data-pair-id') ?? '');
    if (pairId) {
      pairs[pairId] = { ...pairs[pairId], company };
    } else {
      singles.push({ company });
    }
  });

  $('[data-role="assigned-users-body"] tr').each(function() {
    const $tr = $(this);
    const user: AssignedUser = {
      id: String($tr.attr('data-user-id') ?? ''),
      name: String($tr.attr('data-name') ?? ''),
      email: String($tr.attr('data-email') ?? ''),
    };
    const pairId = String($tr.attr('data-pair-id') ?? '');
    if (pairId) {
      pairs[pairId] = { ...pairs[pairId], user };
    } else {
      singles.push({ user });
    }
  });

  return [...Object.values(pairs), ...singles];
}

export function refreshAssignmentData(): void {
  if (!companyInf) return;
  setAssignError(null);
  companyInf.reset();
  userInf?.reset();
}

export function resetAssignment(): void {
  $('[data-role="assigned-companies-body"]').empty();
  $('[data-role="assigned-users-body"]').empty();
  refreshAssignedTables();
  refreshDropdowns();
  setAssignError(null);
}

export function destroyAssignmentDropdowns(): void {
  if (companyInf) {
    companyInf.destroy();
    companyInf = null;
  }
  if (userInf) {
    userInf.destroy();
    userInf = null;
  }
  userFilterCompanyId = '';
}

export function initAssigmentDropdowns(): void {
  const companyEl = document.querySelector<HTMLSelectElement>('[data-role="assign-company"]');
  const userEl = document.querySelector<HTMLSelectElement>('[data-role="assign-user"]');
  if (!companyEl || !userEl) return;

  if (companyInf && companyInf.isStale()) {
    companyInf.destroy();
    companyInf = null;
  }
  if (userInf && userInf.isStale()) {
    userInf.destroy();
    userInf = null;
  }

  if (!companyInf) {
    companyInf = new InfiniteChoices({
      el: companyEl,
      url: COMPANIES_URL,
      placeholder: COMPANY_PLACEHOLDER,
      searchPlaceholder: 'Search companies...',
      buildLabel: company => company.name,
      excludeIds: assignedCompanyIds,
      onError: () => setAssignError('Failed to load companies.'),
      onChange: syncUsersToSelectedCompany,
    });
    companyInf.init();
  }

  if (!userInf) {
    userInf = new InfiniteChoices({
      el: userEl,
      url: ASSIGNEES_URL,
      placeholder: USER_PLACEHOLDER,
      searchPlaceholder: 'Search users...',
      buildLabel: user => (user.email ? `${user.name} - ${user.email}` : user.name),
      extraParams: (): any => (userFilterCompanyId ? { company_id: userFilterCompanyId } : {}),
      excludeIds: assignedUserIds,
      onError: () => setAssignError('Failed to load users.'),
    });
    userInf.init();
  }

  $('[data-role="assign-add"]').off('click.assign').on('click.assign', onAssign);
  $('[data-role="assign-cancel"]').off('click.assign').on('click.assign', function() {
    setAssignError(null);
    refreshDropdowns();
  });

  $(document)
    .off('click.assignRemove')
    .on('click.assignRemove', '#assign-user-step .assign-remove', function() {
      const $tr = $(this).closest('tr');
      const pairId = String($tr.attr('data-pair-id') ?? '');
      if (pairId) {
        $(`#assign-user-step tr[data-pair-id="${pairId}"]`).remove();
      } else {
        $tr.remove();
      }
      refreshAssignedTables();
      refreshDropdowns();
    });
}
